import { useEditContext } from '@components/utils/contexts/EditContext';
import styled from '@emotion/styled';
import {
    StoryPreviewFragment,
    StoryToPlayFragment,
} from '@graphql/generated/graphql';
import { FC, useMemo } from 'react';

import { CurrentStoryPageWrapper } from './components/layout/CurrentStoryPageWrapper';
import { NavigationProvider } from './contexts/NavigationContext';
import { PageProvider } from './contexts/PageContext';
import { ProgressProvider } from './contexts/ProgressContext';
import { StoryProvider, useStoryContext } from './contexts/StoryContext';
import { StoryPageDisplay } from './enums';
import { Renderer, RendererWithTester, SanitizedStoryPage } from './types';
import { getRenderer } from './utils/getRenderer';
import { getSanitizedStoryPages } from './utils/getSanitizedStoryPages';

interface Props {
    story: StoryToPlayFragment;
    storyPages?: SanitizedStoryPage[];
    previousStory?: StoryPreviewFragment;
    nextStory?: StoryPreviewFragment;
    pageIndex?: number;
    module?: string;
    storyPageDisplay?: StoryPageDisplay;
    renderers: RendererWithTester[];
}

interface StoryPageWithRenderer extends SanitizedStoryPage {
    renderer: Renderer;
    originalIndex: number;
}

const Background = styled.div`
    width: 100%;
    height: 100%;
    background: ${(props) => props.theme.palette.grey[900]};
`;

const StoryContent: FC<
    Omit<Props, 'storyPages' | 'module' | 'previousStory' | 'nextStory'>
> = ({
    story,
    pageIndex,
    renderers,
    storyPageDisplay = StoryPageDisplay.Default,
}) => {
    const { isEditMode } = useEditContext();
    const { getActiveStoryPages, storyPages } = useStoryContext();

    const activeStoryPages = getActiveStoryPages();

    const _storyPages = useMemo(() => {
        if (isEditMode) {
            return activeStoryPages.map((sp) => ({ ...sp, looping: true }));
        }

        return activeStoryPages;
    }, [isEditMode, activeStoryPages]);

    const pageWithRenderers = useMemo<StoryPageWithRenderer[]>(
        () =>
            _storyPages.map((storyPage, activeIndex) => {
                // Find the original index in the full story pages array
                const originalIndex = storyPages.findIndex(
                    (originalPage: SanitizedStoryPage, index: number) =>
                        originalPage === storyPage
                );

                return {
                    ...storyPage,
                    originalIndex:
                        originalIndex >= 0 ? originalIndex : activeIndex,
                    renderer: getRenderer({
                        renderers,
                        storyPage,
                        storyPageDisplay,
                    }),
                };
            }),
        [_storyPages, storyPageDisplay, renderers, storyPages]
    );

    return (
        <ProgressProvider pageIndex={pageIndex}>
            <NavigationProvider>
                <Background>
                    {pageWithRenderers.map(
                        (
                            { renderer: Renderer, originalIndex, ...storyPage },
                            index
                        ) => (
                            <PageProvider
                                key={`${originalIndex}-${index}`}
                                pageIndex={index}
                            >
                                <CurrentStoryPageWrapper pageIndex={index}>
                                    <Renderer
                                        story={story}
                                        storyPage={storyPage}
                                    />
                                </CurrentStoryPageWrapper>
                            </PageProvider>
                        )
                    )}
                </Background>
            </NavigationProvider>
        </ProgressProvider>
    );
};

const Story: FC<Props> = ({
    story,
    previousStory,
    nextStory,
    pageIndex,
    module,
    renderers,
    storyPageDisplay = StoryPageDisplay.Default,
    storyPages = getSanitizedStoryPages(story),
}) => {
    return (
        <StoryProvider
            module={module}
            story={story}
            storyPages={storyPages}
            previousStory={previousStory}
            nextStory={nextStory}
        >
            <StoryContent
                story={story}
                pageIndex={pageIndex}
                renderers={renderers}
                storyPageDisplay={storyPageDisplay}
            />
        </StoryProvider>
    );
};

export default Story;
